import * as React from 'react';
import { useNavigate, useLocation } from 'react-router';
import type { PiletApi } from '@chess/platform-web';
import { FILTER_CRITERIA, filterValues, formatFilter, isEmptyFilter, parseFilter, withFilterValues, type FilterKey } from '@chess/api-games/filter';
import { type FilterableGame, type GameFilter, type GameStore, type StoreState } from '@chess/api-model';
import { useStoreQuery } from '@chess/api-model/react';
import { FilterBar, type OptionCounts } from './FilterBar.tsx';
import './filter.css';

// The library's filter, above the game table, over the last 30 days the
// game store holds (the library shows no other games). The filter lives in the
// library's URL query (`?result=loss,draw&colour=black&time=blitz&opponent=…`),
// parsed and formatted by `@chess/api-games/filter`; the game table reads the
// same query, so the two never import each other and any link into the
// library (the home dashboard's charts) can open it already filtered. Changes
// replace the query rather than pushing, so the back button leaves the library
// instead of stepping through filter edits.

const SLOT = 'library.filters';

/**
 * Each choice's count: the games the filter would show with that choice
 * alone for its criterion and every other criterion as it is, so a count is
 * what ticking the box (on its own) gives.
 */
function optionCounts(store: GameStore, filter: GameFilter): OptionCounts {
  const counts: Record<string, Record<string, number>> = {};
  for (const c of FILTER_CRITERIA) {
    if (c.control?.kind !== 'choice') continue;
    const values = new Set<string>([...c.control.options.map(([v]) => String(v)), ...filterValues(filter, c)]);
    counts[c.key] = {};
    for (const v of values) counts[c.key][v] = store.count({ filter: store.resolveFilter(withFilterValues(filter, c, [v])) });
  }
  return counts as OptionCounts;
}

/** Each search field's suggestions, from the games the library holds. */
function suggestionsOf(state: StoreState): Partial<Record<FilterKey, readonly string[]>> {
  const games: FilterableGame[] = state.games.map((g) => ({ ...g.summary, pgn: g.headers }));
  const out: Partial<Record<FilterKey, readonly string[]>> = {};
  for (const c of FILTER_CRITERIA) if (c.control?.kind === 'text' && c.control.suggest) out[c.key] = c.control.suggest(games);
  return out;
}

export function setup(app: PiletApi) {
  const getApi = <T,>(name: string) => app.getApi<T>(name);
  const Container: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const filter = React.useMemo(() => parseFilter(location.search), [location.search]);
    const key = formatFilter(filter);
    // How many games the filter keeps, asked of the store directly so this
    // plugin never depends on the table; recomputed as the store changes (a
    // scan answers a blunder filter). Nothing while the filter is empty.
    const count = useStoreQuery(
      getApi,
      (store) => (isEmptyFilter(filter) ? undefined : store.count({ filter: store.resolveFilter(filter) })),
      [key],
    );
    const change = (next: GameFilter) => {
      const search = formatFilter(next);
      if (search !== key) navigate({ pathname: location.pathname, search, hash: location.hash }, { replace: true, preventScrollReset: true });
    };
    const counts = useStoreQuery(getApi, (store) => optionCounts(store, filter), [key]);
    const suggestions = useStoreQuery(getApi, (_store, state) => suggestionsOf(state), []);
    return <FilterBar filter={filter} count={count} counts={counts} suggestions={suggestions} onChange={change} />;
  };
  app.registerExtension(SLOT, Container);
}
